import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Grid,
  Container,
  Fade,
  Avatar,
  Divider,
  Stack,
  Tooltip,
  Badge,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { departmentService } from '../services/departmentService';
import { employeeService } from '../services/employeeService';
import { Department, Employee } from '../types';
import { useAuth } from '../context/AuthContext';

const DepartmentsPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentDepartment, setCurrentDepartment] = useState<Partial<Department>>({});
  const [departmentEmployees, setDepartmentEmployees] = useState<Employee[]>([]);
  const [openEmployeesDialog, setOpenEmployeesDialog] = useState(false);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const departmentsData = await departmentService.getAll();
        const employeesData = await employeeService.getAll();
        setDepartments(departmentsData);
        setEmployees(employeesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setCurrentDepartment({});
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (department: Department) => {
    setDialogMode('edit');
    setCurrentDepartment({ ...department });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentDepartment({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentDepartment(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDepartment = async () => {
    try {
      if (dialogMode === 'add') {
        await departmentService.create(currentDepartment as Omit<Department, 'id' | 'created_at' | 'updated_at'>);
      } else {
        if (currentDepartment.id) {
          await departmentService.update(currentDepartment.id, currentDepartment);
        }
      }

      // Refresh departments list
      const departmentsData = await departmentService.getAll();
      setDepartments(departmentsData);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving department:', error);
    }
  };

  const handleDeleteDepartment = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        await departmentService.delete(id);
        // Refresh departments list
        const departmentsData = await departmentService.getAll();
        setDepartments(departmentsData);
      } catch (error) {
        console.error('Error deleting department:', error);
      }
    }
  };

  const handleViewEmployees = (departmentId: string, departmentName: string) => {
    // Filter employees by department name (not ID)
    const deptEmployees = employees.filter(emp => emp.department === departmentName);
    setDepartmentEmployees(deptEmployees);
    setSelectedDepartmentName(departmentName);
    setOpenEmployeesDialog(true);
  };

  const handleCloseEmployeesDialog = () => {
    setOpenEmployeesDialog(false);
    setDepartmentEmployees([]);
    setSelectedDepartmentName('');
  };

  // Count employees in each department
  const getEmployeeCount = (departmentId: string): number => {
    // Find the department name by ID
    const department = departments.find(dept => dept.id === departmentId);
    if (!department) return 0;
    
    // Filter employees by department name (not ID)
    return employees.filter(emp => emp.department === department.name).length;
  };

  // Paginate departments
  const paginatedDepartments = departments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={loading}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '60vh',
            gap: 2
          }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#2563eb' }} />
            <Typography variant="h6" color="text.secondary">
              Loading departments...
            </Typography>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #1e40af 100%)',
          color: 'white',
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2), 0 8px 16px rgba(124, 58, 237, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            opacity: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <BusinessIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                  Department Management
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Organize and manage your company's departments and structure
                </Typography>
              </Box>
            </Stack>
          {isAdmin() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Add Department
            </Button>
          )}
          </Box>
        </CardContent>
      </Card>

      {/* Department Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {departments.slice(0, 4).map((department, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={department.id}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${[
                  'rgba(37, 99, 235, 0.1)',
                  'rgba(124, 58, 237, 0.1)',
                  'rgba(16, 185, 129, 0.1)',
                  'rgba(245, 158, 11, 0.1)'
                ][index % 4]} 0%, rgba(255, 255, 255, 0.05) 100%)`,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background: `linear-gradient(135deg, ${[
                        '#2563eb',
                        '#7c3aed',
                        '#10b981',
                        '#f59e0b'
                      ][index % 4]} 0%, ${[
                        '#1e40af',
                        '#5b21b6',
                        '#047857',
                        '#d97706'
                      ][index % 4]} 100%)`,
                      color: 'white',
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {department.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Badge
                        badgeContent={getEmployeeCount(department.id)}
                        color="primary"
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: [
                              '#2563eb',
                              '#7c3aed',
                              '#10b981',
                              '#f59e0b'
                            ][index % 4],
                            right: -3,
                            top: 3,
                            fontSize: '0.75rem',
                            minWidth: '20px',
                            height: '20px',
                            borderRadius: '10px',
                          },
                        }}
                      >
                        <PeopleIcon sx={{ color: 'text.secondary' }} />
                      </Badge>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Employees
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
                {department.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    {department.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Departments Table */}
      <Card sx={{ overflow: 'hidden', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Employees</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDepartments.map((department) => (
                <TableRow 
                  key={department.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, backgroundColor: '#2563eb' }}>
                        <BusinessIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {department.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {department.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View department employees">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PeopleIcon />}
                        onClick={() => handleViewEmployees(department.id, department.name)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          minWidth: 'auto',
                          px: 2,
                        }}
                      >
                        {getEmployeeCount(department.id)}
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {isAdmin() && (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit department">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(department)}
                            sx={{
                              color: '#2563eb',
                              '&:hover': {
                                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete department">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDepartment(department.id)}
                            sx={{
                              color: '#dc2626',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={departments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Card>

      {/* Add/Edit Department Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
            <BusinessIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {dialogMode === 'add' ? 'Add Department' : 'Edit Department'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mt: 2 }}>
            {/* Department Information Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ width: 32, height: 32, backgroundColor: '#2563eb', mr: 2 }}>
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                  Department Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    name="name"
                    label="Department Name"
                    value={currentDepartment.name || ''}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: <WorkIcon sx={{ color: '#6b7280', mr: 1 }} />,
                    }}
                  />
                  <TextField
                    name="description"
                    label="Description"
                    value={currentDepartment.description || ''}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    fullWidth
                    InputProps={{
                      startAdornment: <DescriptionIcon sx={{ color: '#6b7280', mr: 1, alignSelf: 'flex-start', mt: 1 }} />,
                    }}
                  />
                </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveDepartment} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)',
              },
            }}
          >
            Save Department
          </Button>
        </DialogActions>
      </Dialog>

      {/* Department Employees Dialog */}
      <Dialog 
        open={openEmployeesDialog} 
        onClose={handleCloseEmployeesDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
            <PeopleIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedDepartmentName} - Employees
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {departmentEmployees.length > 0 ? (
            <Card sx={{ boxShadow: 'none' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Position</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentEmployees.map((employee, index) => (
                      <TableRow 
                        key={employee.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f8fafc',
                          },
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 32, height: 32, backgroundColor: '#2563eb' }}>
                              {employee.first_name.charAt(0)}{employee.last_name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {`${employee.first_name} ${employee.last_name}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{employee.email}</TableCell>
                        <TableCell>
                          <Tooltip title="Position">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WorkIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                              {employee.position}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{employee.phone || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Avatar sx={{ 
                width: 64, 
                height: 64, 
                backgroundColor: '#f3f4f6', 
                color: '#9ca3af',
                mx: 'auto',
                mb: 2
              }}>
                <PeopleIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                No Employees Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                This department doesn't have any employees assigned yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button 
            onClick={handleCloseEmployeesDialog}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DepartmentsPage;